import {
  trigger,
  transition,
  style,
  animate,
  query,
  stagger,
  state,
  animateChild,
  group,
} from '@angular/animations';

/**
 * Route transition animation.
 *
 * Usage in the host component template:
 *   <div [@routeAnimation]="prepareRoute(outlet)">
 *     <router-outlet #outlet="outlet"></router-outlet>
 *   </div>
 *
 * The `prepareRoute` helper should return the activated route's data
 * (e.g. `outlet.activatedRouteData['animation']` or `outlet.isActivated`).
 */
export const routeAnimation = trigger('routeAnimation', [
  transition('* <=> *', [
    // Style the incoming page before it enters
    query(
      ':enter',
      [
        style({
          opacity: 0,
          transform: 'translateY(20px)',
        }),
      ],
      { optional: true }
    ),
    // Animate the outgoing page and then the incoming page
    group([
      query(
        ':leave',
        [animate('300ms ease-out', style({ opacity: 0, transform: 'translateY(20px)' }))],
        { optional: true }
      ),
      query(
        ':enter',
        [animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))],
        { optional: true }
      ),
    ]),
  ]),
]);

/**
 * Simple fade-in / fade-out for elements that appear and disappear.
 *
 * Usage: <div *ngIf="visible" @fadeIn>...</div>
 */
export const fadeIn = trigger('fadeIn', [
  transition(':enter', [
    style({ opacity: 0 }),
    animate('200ms ease-out', style({ opacity: 1 })),
  ]),
  transition(':leave', [
    style({ opacity: 1 }),
    animate('150ms ease-in', style({ opacity: 0 })),
  ]),
]);

/**
 * Slide-up animation for alerts and notifications.
 *
 * Usage: <div *ngIf="showAlert" @slideUp>...</div>
 */
export const slideUp = trigger('slideUp', [
  transition(':enter', [
    style({ opacity: 0, transform: 'translateY(20px)' }),
    animate('250ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
  ]),
]);

/**
 * Stagger animation for list items.
 * Apply this trigger on the *parent* container; each direct child
 * will fade in and slide up with a 50 ms stagger delay.
 *
 * Usage:
 *   <ul [@listStagger]="items.length">
 *     <li *ngFor="let item of items">{{ item }}</li>
 *   </ul>
 */
export const listStagger = trigger('listStagger', [
  transition('* <=> *', [
    query(
      ':enter',
      [
        style({ opacity: 0, transform: 'translateY(15px)' }),
        stagger('50ms', [
          animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
        ]),
      ],
      { optional: true }
    ),
  ]),
]);

/**
 * Modal open / close animation with scale and opacity.
 *
 * Usage: <div *ngIf="isModalOpen" @modalAnimation>...</div>
 */
export const modalAnimation = trigger('modalAnimation', [
  transition(':enter', [
    style({ opacity: 0, transform: 'scale(0.95)' }),
    animate('200ms ease-out', style({ opacity: 1, transform: 'scale(1)' })),
  ]),
  transition(':leave', [
    style({ opacity: 1, transform: 'scale(1)' }),
    animate('150ms ease-in', style({ opacity: 0, transform: 'scale(0.95)' })),
  ]),
]);

/**
 * Scale-in animation for status badges and small indicators.
 *
 * Usage: <span *ngIf="showBadge" @scaleIn>Active</span>
 */
export const scaleIn = trigger('scaleIn', [
  transition(':enter', [
    style({ opacity: 0, transform: 'scale(0.8)' }),
    animate('200ms ease-out', style({ opacity: 1, transform: 'scale(1)' })),
  ]),
]);
